import * as React from 'react';
import {observer} from 'mobx-react';
import {style} from 'typestyle';
import {ArtistLinkList} from './artist-link-list';
import {WaveformImage} from './waveform-image';
import {
  center,
  centerCenter,
  centerJustified,
  endJustified,
  horizontal,
  horizontallySpaced,
  startJustified,
  vertical,
  verticallySpaced
} from 'csstips';
import {em, important, percent, px} from 'csx';
import {colorBrand, colorSubtle} from '../theme/color';
import {ButtonPill} from './button-pill';
import {TrackTile} from './track-tile';
import {resetAnchorStyles} from '../theme/reset';
import {LicenceSchema, OrderSchema} from '../types/schema';
import {formatCurrency} from '../lib/currency';
import {ImagePreloader} from '../types/fn';

export interface PageAccountOrderLicencesModel {
  order: OrderSchema;
}

export interface PageAccountOrderLicencesProps {
  model: PageAccountOrderLicencesModel;
  onClickLink(event: React.MouseEvent<any>): void;
  onAttribution(licenceId: string): void;
  onDownloadAudio(licenceId: string): void;
  onDownloadEULA(track: LicenceSchema): void;
  isMobile: boolean;
  preload: ImagePreloader;
  preloadWaveform?: ImagePreloader;
  AddRemove: any;
  PlayButton: any;
  i18n?: {
    downloadAudio: string;
    downloadEULA: string;
    preparingEULA: string;
    attribution: string;
    viewOrder: string;
  }
}

const DEFAULT_I18N = {
  downloadAudio: 'Download audio',
  downloadEULA: 'View license',
  preparingEULA: 'Preparing license...',
  attribution: 'View video attribution',
};

export const PageAccountOrderLicences = observer<PageAccountOrderLicencesProps>(
  ({ model, i18n = DEFAULT_I18N, onClickLink, isMobile, preload, preloadWaveform, AddRemove, PlayButton, onDownloadAudio, onDownloadEULA, onAttribution }) => (
    <div className={styles.container}>
      {model.order.licences.map((licence, index) => !isMobile ? (
        <div className={styles.trackDesktop} key={index.toString()}>
          <div>{licence.sequence}</div>
          <div>
            <TrackTile
              track={licence.track}
              preload={preload}
              noMeta={true}
              PlayButton={PlayButton}
              className={styles.tile}
              onClickLink={onClickLink}
            />
          </div>
          <div className={styles.meta}>
            <a
              className={styles.link}
              href={`/music/artists/${licence.track.artists[0].slug}/track/${licence.track.slug}`}
              onClick={onClickLink}
            >{licence.track.title}</a>
            <ArtistLinkList
              model={{ artists: licence.track.artists }}
              onClickLink={onClickLink}
            />
          </div>
          <div>
            {licence.track.audio && licence.track.audio.identity && (
              <WaveformImage identity={licence.track.audio.identity} preloadWaveform={preloadWaveform} />
            )}
          </div>
          <div>
            {formatCurrency(licence.amount, model.order.locale.currency)}
          </div>
          <div>
            <AddRemove track={licence.track} />
          </div>
          <div className={styles.actions}>
            <ButtonPill muted={true} onClick={onAttribution.bind(null, licence.identity)}>{i18n.attribution}</ButtonPill>
            <ButtonPill muted={true} onClick={onDownloadAudio.bind(null, licence.identity)}>{i18n.downloadAudio}</ButtonPill>
            <ButtonPill primary={true} disabled={!licence.eula_html_href} muted={true} href={licence.eula_html_href} target="_blank">{licence.eula_html_href ? i18n.downloadEULA : i18n.preparingEULA}</ButtonPill>
          </div>
        </div>
      ) : (
        <div className={styles.trackMobile} key={index.toString()}>
          <div>
            <div className={styles.mobileMeta}>
              <div>
                <TrackTile
                  track={licence.track}
                  preload={preload}
                  noMeta={true}
                  PlayButton={PlayButton}
                  AddRemove={AddRemove}
                  className={styles.tile}
                  onClickLink={onClickLink}
                />
              </div>
            </div>
            <div className={styles.actions}>
              <ButtonPill muted={true} onClick={onAttribution.bind(null, licence.identity)}>{i18n.attribution}</ButtonPill>
              <ButtonPill muted={true} onClick={onDownloadAudio.bind(null, licence.identity)}>{i18n.downloadAudio}</ButtonPill>
              <ButtonPill primary={true} disabled={!licence.eula_html_href} muted={true} href={licence.eula_html_href} target="_blank">{licence.eula_html_href ? i18n.downloadEULA : i18n.preparingEULA}</ButtonPill>
            </div>
          </div>
          <div>
            <div>{licence.sequence}</div>
            <div className={styles.meta}>
              <div>{licence.track.title}</div>
              <ArtistLinkList
                model={{ artists: licence.track.artists }}
                onClickLink={onClickLink}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  )
);

const tileSize = px(100);

const styles = {
  container: style({
    position: 'relative',
    width: percent(100)
  }),
  link: style({
    color: colorBrand.toString(),
    $nest: { ...resetAnchorStyles(colorBrand.toString()) }
  }),
  trackDesktop: style({
    ...horizontal,
    ...horizontallySpaced(20),
    ...center,
    padding: '10px 0',
    $nest: {
      '&> *:nth-child(1)': {
        ...horizontal,
        ...startJustified,
        minWidth: px(32),
        fontSize: px(18),
        fontWeight: 'bold'
      },
      '&> *:nth-child(2)': {
        ...horizontal,
        ...startJustified,
        width: tileSize,
        height: tileSize
      },
      '&> *:nth-child(3)': {
        flex: 2,
        overflow: 'hidden'
      },
      '&> *:nth-child(4)': {
        ...centerCenter,
        position: 'relative',
        flex: 3
      },
      '&> *:nth-child(4) img': {
        width: percent(100),
        height: px(30),
        borderRadius: px(50)
      },
      '&> *:nth-child(5)': {
        minWidth: px(120),
        ...horizontal,
        ...endJustified,
      },
      '&> *:nth-child(6)': {
        ...horizontal,
        ...endJustified,
        minWidth: px(32),
      }
    }
  }),
  trackMobile: style({
    ...horizontal,
    ...horizontallySpaced(20),
    ...center,
    padding: '10px 0',
    $nest: {
      '&> *:nth-child(1)': {
        ...horizontal,
        ...startJustified,
        minWidth: px(32),
        fontSize: px(18),
        fontWeight: 'bold'
      },
      '&> *:nth-child(2)': {
        ...horizontal,
        ...startJustified,
        width: tileSize,
        height: tileSize
      },
      '&> *:nth-child(3)': {
        flex: 2,
        overflow: 'hidden'
      },
      '&> *:nth-child(4)': {
        ...centerCenter,
        position: 'relative',
        flex: 3
      },
      '&> *:nth-child(4) img': {
        width: percent(100),
        height: px(30),
        borderRadius: px(50)
      },
      '&> *:nth-child(5)': {
        minWidth: px(120),
        ...horizontal,
        ...endJustified,
      },
      '&> *:nth-child(6)': {
        ...horizontal,
        ...endJustified,
        minWidth: px(32),
      }
    }
  }),
  tile: style({
    width: tileSize
  }),
  meta: style({
    lineHeight: em(1.1),
    fontSize: px(16),
    overflow: 'hidden',
    $nest: {
      '&> div': {
        overflow: 'hidden',
        whiteSpace: 'pre',
        textOverflow: 'ellipsis'
      },
      '&> div:last-child': {
        fontSize: px(12),
        color: colorSubtle.toString()
      },
      '& a': {
        fontSize: px(12),
        color: important(colorSubtle.toString())
      },
      '& a:hover': {
        color: important(colorBrand.toString())
      }
    }
  }),
  mobileMeta: style({
    ...horizontal,
    ...horizontallySpaced(20),
    ...center,
    flex: 1,
    $nest: {
      '&> *:nth-child(1)': {
        ...startJustified,
        minWidth: px(32),
        fontSize: px(18),
        fontWeight: 'bold'
      },
      '&> *:nth-child(2)': {
        ...horizontal,
        ...startJustified,
        minWidth: px(32),
      },
      '&> *:nth-child(3)': {
        flex: 1,
        ...horizontal,
        ...endJustified,
      },
      '&> *:nth-child(4)': {
        ...horizontal,
        ...endJustified,
        minWidth: px(32),
      }
    }
  }),
  actions: style({
    ...vertical,
    ...verticallySpaced(8),
    ...centerJustified,
    height: tileSize,
    $nest: {
      'div': {
        fontSize: px(12)
      },
      'div svg': {
        marginLeft: px(10)
      },
      'a': {
        fontSize: px(12)
      },
      'a svg': {
        marginLeft: px(10)
      }
    }
  })
};